import { useState, useCallback, useEffect } from 'react';
import {
  MessageThread,
  ThreadDetail,
  ThreadMessage,
  GetMessagesQuery,
  ReplyMessageRequest,
  MessageStatus,
  ReplyType,
} from '../types/message-reply';
import { messageReplyService } from '../services/messageReplyService';
import { useToast } from './useToast';

export const useMessageReply = () => {
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [currentThread, setCurrentThread] = useState<ThreadDetail | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  // 返信テンプレート機能は削除されました
  const [loading, setLoading] = useState(false);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
    has_next: false,
    has_prev: false,
  });
  const [summary, setSummary] = useState({
    total_unread: 0,
    total_starred: 0,
    total_archived: 0,
    new_this_week: 0,
  });
  const { showToast } = useToast();

  // メッセージ一覧取得
  const loadMessages = useCallback(
    async (query: GetMessagesQuery = {}) => {
      try {
        setThreadsLoading(true);
        const response = await messageReplyService.getMessages(query);

        setThreads(response.data.messages); // バックエンドは 'messages' フィールド
        setPagination({
          current_page: response.data.pagination.current_page,
          total_pages: response.data.pagination.total_pages,
          total_items: response.data.pagination.total_count, // バックエンドは 'total_count'
          items_per_page: response.data.pagination.per_page, // バックエンドは 'per_page'
          has_next:
            response.data.pagination.current_page <
            response.data.pagination.total_pages,
          has_prev: response.data.pagination.current_page > 1,
        });
        setSummary({
          total_unread: response.data.summary.total_unread,
          total_starred: 0, // バックエンドにはないフィールド
          total_archived: 0, // バックエンドにはないフィールド
          new_this_week: response.data.summary.new_this_week,
        });
      } catch (error) {
        console.error('Failed to load messages:', error);
        showToast('メッセージ一覧の取得に失敗しました', 'error');
      } finally {
        setThreadsLoading(false);
      }
    },
    [showToast]
  );

  // スレッド詳細取得
  const loadThreadDetail = useCallback(
    async (threadId: string) => {
      try {
        setLoading(true);
        const response = await messageReplyService.getMessageDetail(threadId);

        setCurrentThread(response.data.message); // バックエンドは 'message' フィールド

        // 元のメッセージ（企業からのスカウト）と返信を統合
        const originalMessage: ThreadMessage = {
          id: response.data.message.id as any,
          sender_type: 'company',
          sender: (response.data.message as any).sender,
          message_body: (response.data.message as any).message_body,
          sent_at: (response.data.message as any).sent_at,
          read_at: (response.data.message as any).read_at,
          is_ai_assisted: false,
        };

        // 返信メッセージを変換
        const replyMessages: ThreadMessage[] = response.data.replies.map(
          (reply: any) => ({
            id: reply.id,
            sender_type: 'student',
            sender: {
              name: reply.sender_name,
            },
            message_body: reply.message_body,
            sent_at: reply.sent_at,
            read_at: reply.read_at,
            is_ai_assisted: false,
          })
        );

        // 時系列順に並べる（元のメッセージが最初、その後返信）
        const allMessages = [originalMessage, ...replyMessages];
        setMessages(allMessages);

        // スレッド一覧の既読状態を更新
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? { ...thread, status: MessageStatus.READ, unread_count: 0 }
              : thread
          )
        );
      } catch (error) {
        console.error('Failed to load thread detail:', error);
        showToast('メッセージ詳細の取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  // メッセージ返信
  const sendReply = useCallback(
    async (threadId: string, replyData: ReplyMessageRequest) => {
      try {
        setReplyLoading(true);
        const response = await messageReplyService.replyToMessage(
          threadId,
          replyData
        );

        // 新しいメッセージを追加
        const newMessage: ThreadMessage = {
          id: response.data.reply_id, // バックエンドは 'reply_id'
          sender_type: 'student',
          sender: {
            name: '学生太郎', // TODO: 実際のユーザー名を取得
            avatar_url: undefined,
          },
          message_body: replyData.message_body,
          reply_type: ReplyType.CUSTOM, // 常にCUSTOM
          sent_at: response.data.sent_at, // バックエンドから取得
          is_ai_assisted: false, // AI支援機能は削除
        };

        setMessages((prev) => [...prev, newMessage]);

        // スレッド一覧の状態を更新
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  status: MessageStatus.REPLIED,
                  message_count: thread.message_count + 1,
                  last_message_at: newMessage.sent_at,
                  latest_message: {
                    preview: replyData.message_body.substring(0, 50) + '...',
                    sent_at: newMessage.sent_at,
                    sender_type: 'student',
                  },
                }
              : thread
          )
        );

        showToast('返信を送信しました', 'success');
        return response.data;
      } catch (error: any) {
        console.error('Failed to send reply:', error);

        // バックエンドからのエラーメッセージを抽出
        let errorMessage = '返信の送信に失敗しました';
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.errors) {
          // バリデーションエラーの場合
          const validationErrors = error.response.data.errors
            .map((err: any) => err.messages.join(', '))
            .join(', ');
          errorMessage = `入力エラー: ${validationErrors}`;
        }

        showToast(errorMessage, 'error');
        throw error;
      } finally {
        setReplyLoading(false);
      }
    },
    [showToast]
  );

  // 返信テンプレート取得
  // 返信テンプレート機能は削除されました

  // テンプレートのテキスト処理
  // 返信テンプレート機能は削除されました

  // 初期データ読み込み
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  return {
    // State
    threads,
    currentThread,
    messages,
    // templates は削除
    loading,
    threadsLoading,
    replyLoading,
    pagination,
    summary,

    // Actions
    loadMessages,
    loadThreadDetail,
    sendReply,
    // loadTemplates, processTemplate は削除

    // Utilities
    setCurrentThread,
    setMessages,
  };
};
